import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { TiltCard } from "@/components/TiltCard";
import { GALLERY_ITEMS } from "@/data/content";
import { cn } from "@/lib/utils";

export const Gallery = () => {
    const navigate = useNavigate();

    return (
        <section id="gallery" data-testid="gallery-section" className="blueprint noise relative overflow-hidden py-24 lg:py-36">
            <div className="pointer-events-none absolute -right-40 top-24 h-[460px] w-[460px] rounded-full bg-omsivo-blue/30 blur-[140px]" />
            <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                <SectionHeading
                    id="gallery-heading"
                    dark
                    overline="3D / Concept Gallery"
                    title={<>Step inside the work, <span className="text-flame">before it exists.</span></>}
                    description="Interactive concept visualizations across every OMSIVO category. Hover or tap any frame to reveal its layers — structure, engineering, interior, material, design."
                />
                <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {GALLERY_ITEMS.map((g, i) => (
                        <Reveal key={g.slug} delay={0.05 * (i % 3)} className={cn(i === 0 && "sm:col-span-2 lg:row-span-2")}>
                            <TiltCard
                                testid={`gallery-card-${g.slug}`}
                                onClick={() => navigate(`/services/${g.slug}`)}
                                className={cn("group h-full", i === 0 && "lg:h-full")}
                            >
                                <div className={cn("relative h-full min-h-[260px] overflow-hidden rounded-2xl border border-white/10", i === 0 && "lg:min-h-[540px]")}>
                                    <img src={g.img} alt={`${g.title} concept visualization`} loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] group-data-[active=true]:scale-[1.06]" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-omsivo-deep/95 via-omsivo-deep/25 to-omsivo-deep/10" />
                                    <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-omsivo-deep/70 px-3 py-1.5 font-mono text-[8px] font-semibold uppercase tracking-[0.22em] text-white/70 backdrop-blur">
                                        Concept Visualization
                                    </span>
                                    <div className="absolute inset-x-0 bottom-0 p-5">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-display text-lg font-extrabold uppercase tracking-[0.06em] text-white">{g.title}</h3>
                                            <ArrowUpRight className="h-5 w-5 text-flame transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                        </div>
                                        <div className="mt-0 flex max-h-0 flex-wrap gap-1.5 overflow-hidden opacity-0 transition-all duration-500 group-hover:mt-3 group-hover:max-h-16 group-hover:opacity-100 group-data-[active=true]:mt-3 group-data-[active=true]:max-h-16 group-data-[active=true]:opacity-100">
                                            {g.tags.map((t) => (
                                                <span key={t} className="rounded-full border border-flame/40 bg-flame/10 px-2.5 py-1 font-mono text-[8px] font-semibold uppercase tracking-[0.18em] text-flame">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </TiltCard>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                        All frames are concept visualizations. Real OMSIVO project photography will replace them as the portfolio opens.
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
